import React from 'react';
import PropTypes from 'prop-types';
import { Button, Row, Typography } from '@tempmee/react';

/**
 * Page stepper for the awards-service list endpoints.
 *
 * The design system has no Pagination component, and every awards-service list
 * fixes pageSize at 15 server-side, so this only ever needs prev/next plus a
 * window description — not a page-number strip.
 */
export const DsPagination = ({ page, pageSize, totalCount, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Hide only when the data fits one page AND we are on it. If a shrunk list
  // stranded us past the last page, keep the stepper so Previous is a way back
  // rather than an empty table with no controls — useClampedPage then corrects
  // the page state, but this holds the escape hatch until it does.
  if (totalPages <= 1 && page <= 1) return null;

  const firstItem = (page - 1) * pageSize + 1;
  const lastItem = Math.min(page * pageSize, totalCount);

  return (
    <Row align="center" justify="between" gap={3} wrap="wrap">
      <Typography variant="body2">
        {`Showing ${firstItem}–${lastItem} of ${totalCount}`}
      </Typography>

      <Row align="center" gap={3}>
        <Button
          variant="outlined"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>

        <Button
          variant="outlined"
          aria-label="Next page"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </Row>
    </Row>
  );
};

DsPagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};
